import classnames from 'classnames';
import * as _ from 'lodash';
import { memo } from 'react';
import type { ChangeEvent, FocusEventHandler, KeyboardEvent } from 'react';

import type { ChildrenAsFunction } from '../../../types';

export type InputElement = HTMLInputElement | HTMLTextAreaElement;

export type TextInputOnChange = (value: string, event?: ChangeEvent<InputElement>) => void;

//  Props passed directly to InputBase
export type InputBaseProps = {
  /**
   * Indicates what HTML element is being used. @default 'input'
   */
  element?: 'input' | 'textarea';
  /**
   * The name of the input
   */
  name?: string;
  /**
   * An a11y label describing the input.
   */
  label: string;
  /**
   * The value of the input.
   */
  value?: string | number;
  /**
   * onChange event handler, which receives the value and then the event object.
   */
  onChange?: TextInputOnChange;
  /**
   * Type of text input. @default 'text'
   */
  type?: 'text' | 'password' | 'email' | 'search' | 'number';
  /**
   * Size for the input @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * ID for the input. Optional, but helps if using a label.
   */
  id?: string;
  /**
   * Placeholder text for the input.
   */
  placeholder?: string;
  /**
   * Mark the input as required.
   */
  isRequired?: boolean;
  /**
   * If the input is disabled or not.
   */
  isDisabled?: boolean;
  /**
   * Similar to disabled, but the input can still be focused and contents copied.
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#readonly
   */
  isReadOnly?: boolean;
  /**
   * Adds a `data-test-id` attribute, useful for browser (Playwright) tests.
   */
  testId?: string;
  /**
   * Called when a key is pressed while the input is in focus.
   */
  onKeyDown?: (event: KeyboardEvent<InputElement>) => void;
  /**
   * Max length of the input/textarea. See: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/maxlength
   */
  maxLength?: number;
  /**
   * If the input should be auto-focused on mount.
   */
  autoFocus?: boolean;
  /**
   * If the textarea is resizable or not.
   */
  isResizable?: boolean;
  /**
   * If the input can be copied to the clipboard.
   */
  isCopyable?: boolean;
  /**
   * Children-as-a-function, which gets the render props to be spread onto an input component.
   */
  children: ChildrenAsFunction<InputRenderProps>;
  /**
   * The title element to populate the tooltip.
   */
  title?: string;
  /**
   * Called when the input is no longer focused.
   */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * Called when the input is focused.
   */
  onFocus?: FocusEventHandler<HTMLInputElement>;
};

//  These are the render props exposed by this component, spread onto the consuming <input /> or <textarea />
export type InputRenderProps = {
  type: 'text' | 'password' | 'email' | 'search' | 'number';
  value: number | string;
  className: string;
  id?: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  maxLength?: number;
  autoFocus?: boolean;
  placeholder?: string;
  'aria-label': string;
  'aria-required'?: boolean;
  'data-test-id'?: string;
  'data-testid'?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  title?: string;
  name?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
};

/**
 * A generic, render prop container for input & textarea components.
 * This allows us to build custom inputs while maintaining the correct styles, classes, and a11y.
 */
export const InputBase = ({
  children,
  element = 'input',
  type = 'text',
  label,
  value = '',
  onChange = _.noop,
  size = 'medium',
  id,
  placeholder,
  isRequired,
  isDisabled,
  isReadOnly,
  onKeyDown,
  testId,
  maxLength,
  autoFocus,
  isResizable,
  isCopyable,
  title,
  name,
  onBlur,
  onFocus,
}: InputBaseProps) => children?.({
  type,
  value,
  placeholder,
  'aria-label': label,
  className: classnames({
    [`fossa-form__text-input fossa-form__text-input--${size}`]: element === 'input',
    [`fossa-form__text-area fossa-form__text-area--${size}`]: element === 'textarea',
    'fossa-form__search-input': type === 'search',
    'fossa-form__text-area--resizable': (element === 'textarea' && isResizable),
    'fossa-form__text-input--isCopyable': isCopyable,
    'fossa-form__text-input--isReadOnly': isReadOnly,
  }),
  id,
  onChange: (event: ChangeEvent<InputElement>) => onChange(event.target.value, event),
  onKeyDown,
  'aria-required': isRequired,
  'data-test-id': testId,
  'data-testid': testId,
  required: isRequired,
  disabled: isDisabled,
  readOnly: isReadOnly,
  maxLength,
  autoFocus,
  title,
  name,
  onBlur,
  onFocus,
});

export default memo(InputBase);
